import { useEffect, useRef, useState, type FormEvent } from "react";
import { storeRequest } from "../lib/mini-app-api";
import { supabase } from "../lib/supabase";

type Props = {
  open: boolean;
  accountEmail?: string;
  accountHandle?: string | null;
  accessToken?: string;
  isReseller?: boolean;
  onProfileUpdated?(telegramHandle: string): void;
  onClose(): void;
};

const normalizeHandle = (value: string) => value.trim().replace(/^@/, "").toLowerCase();
const validHandle = (value: string) => /^[a-z0-9_]{5,32}$/.test(normalizeHandle(value));

const authError = (message: string) => {
  const lower = message.toLowerCase();
  if (lower.includes("invalid login credentials")) return "Email ou palavra-passe incorretos.";
  if (lower.includes("user already registered")) return "Já existe uma conta com este email.";
  if (lower.includes("password")) return "Use uma palavra-passe com pelo menos 8 caracteres.";
  if (lower.includes("rate limit")) return "Foram feitas demasiadas tentativas. Aguarde alguns minutos.";
  if (lower.includes("já está associado")) return "Este utilizador Telegram já está associado a outra conta.";
  return "Não foi possível concluir o acesso. Tente novamente.";
};

export function ShopAuth({ open, accountEmail, accountHandle, accessToken, isReseller, onProfileUpdated, onClose }: Props) {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [telegramHandle, setTelegramHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    setTelegramHandle(accountHandle ?? "");
    closeRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { onClose(); return; }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0]!; const last = focusable.at(-1)!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [accountHandle, onClose, open]);

  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError(""); setNotice("");
    const normalizedHandle = normalizeHandle(telegramHandle);
    if (mode === "signup" && normalizedHandle && !validHandle(normalizedHandle)) {
      setError("Use 5–32 letras, números ou underscores no utilizador Telegram.");
      setBusy(false);
      return;
    }
    const result = mode === "signin"
      ? await supabase.auth.signInWithPassword({ email: email.trim(), password })
      : await supabase.auth.signUp({ email: email.trim(), password, options: { data: { display_name: name.trim(), telegram_handle: normalizedHandle || null }, emailRedirectTo: `${window.location.origin}/shop` } });
    if (result.error) setError(authError(result.error.message));
    else if (mode === "signup" && !result.data.session) setNotice("Conta criada. Confirme o email para entrar na loja.");
    else onClose();
    setBusy(false);
  }

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    const normalizedHandle = normalizeHandle(telegramHandle);
    if (!accessToken || !validHandle(normalizedHandle)) {
      setError("Use 5–32 letras, números ou underscores no utilizador Telegram.");
      return;
    }
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await storeRequest<{ telegram_handle: string }>("/store/profile", accessToken, {
        method: "PATCH",
        body: JSON.stringify({ telegram_handle: normalizedHandle }),
      });
      setTelegramHandle(result.telegram_handle);
      onProfileUpdated?.(result.telegram_handle);
      setNotice("Utilizador Telegram guardado.");
    } catch (reason) {
      setError(authError(reason instanceof Error ? reason.message : ""));
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    setBusy(true); setError("");
    const { error: reason } = await supabase.auth.signOut({ scope: "local" });
    if (reason) setError(authError(reason.message)); else onClose();
    setBusy(false);
  }

  return <div className="shop-auth-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={dialogRef} className="shop-auth" role="dialog" aria-modal="true" aria-labelledby="shop-auth-title">
      <div className="shop-auth-head"><div><h2 id="shop-auth-title">{accountEmail ? "A sua conta" : mode === "signin" ? "Entrar na loja" : "Criar conta"}</h2><p>{accountEmail ? accountEmail : "Use email e palavra-passe. Sem bot Telegram."}</p></div><button ref={closeRef} type="button" onClick={onClose} aria-label="Fechar">×</button></div>
      {accountEmail ? <div className="shop-account-actions">
        <p>O carrinho e os pedidos guardados ficam associados a esta conta.</p>
        {isReseller && <p className="shop-account-tier">Acesso de revenda ativo</p>}
        <form onSubmit={(event) => void saveProfile(event)}>
          <label><span>Utilizador Telegram</span><span className="shop-handle-input"><b aria-hidden="true">@</b><input value={telegramHandle} onChange={(event) => setTelegramHandle(event.target.value.replace(/^@/, ""))} autoComplete="off" inputMode="text" minLength={5} maxLength={32} pattern="[A-Za-z0-9_]{5,32}" required/></span><small>Usado apenas para podermos contactá-lo sobre pedidos.</small></label>
          {error && <p className="shop-auth-error" role="alert">{error}</p>}
          {notice && <p className="shop-auth-notice" role="status">{notice}</p>}
          <button className="shop-checkout" type="submit" disabled={busy}>{busy ? "A guardar…" : "Guardar utilizador"}</button>
        </form>
        <button type="button" onClick={() => void signOut()} disabled={busy}>{busy ? "A sair…" : "Terminar sessão"}</button>
      </div> : <>
        <div className="shop-auth-tabs" role="tablist" aria-label="Tipo de acesso"><button type="button" role="tab" aria-selected={mode === "signin"} onClick={() => { setMode("signin"); setError(""); setNotice(""); }}>Entrar</button><button type="button" role="tab" aria-selected={mode === "signup"} onClick={() => { setMode("signup"); setError(""); setNotice(""); }}>Criar conta</button></div>
        <form onSubmit={(event) => void submit(event)}>
          {mode === "signup" && <label><span>Nome</span><input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required maxLength={128}/></label>}
          {mode === "signup" && <label><span>Utilizador Telegram <small>(opcional)</small></span><span className="shop-handle-input"><b aria-hidden="true">@</b><input value={telegramHandle} onChange={(event) => setTelegramHandle(event.target.value.replace(/^@/, ""))} autoComplete="off" inputMode="text" minLength={5} maxLength={32} pattern="[A-Za-z0-9_]{5,32}" placeholder="seu_utilizador"/></span><small>Permite-nos contactá-lo diretamente sobre o pedido.</small></label>}
          <label><span>Email</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required/></label>
          <label><span>Palavra-passe</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === "signin" ? "current-password" : "new-password"} required minLength={8}/></label>
          {error && <p className="shop-auth-error" role="alert">{error}</p>}
          {notice && <p className="shop-auth-notice" role="status">{notice}</p>}
          <button className="shop-checkout" type="submit" disabled={busy}>{busy ? "A processar…" : mode === "signin" ? "Entrar na loja" : "Criar conta"}</button>
        </form>
      </>}
    </section>
  </div>;
}
